import {
  forwardRef,
  useEffect,
  useState,
  type HTMLAttributes,
  type ImgHTMLAttributes,
} from 'react';
import { cn } from '../../../lib/cn.js';

/** Minimal Avatar primitive (Image + Fallback) — no @radix-ui dep. */
export const Avatar = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full', className)}
      {...props}
    />
  ),
);
Avatar.displayName = 'Avatar';

interface AvatarImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  onLoadingStatusChange?: (status: 'loaded' | 'error' | 'loading') => void;
}

export const AvatarImage = forwardRef<HTMLImageElement, AvatarImageProps>(
  ({ className, onLoadingStatusChange, onLoad, onError, src, ...props }, ref) => {
    const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>(src ? 'loading' : 'error');
    // Reset status when `src` changes so a previously-errored URL doesn't
    // permanently hide a subsequent valid URL (common in profile editors).
    useEffect(() => {
      setStatus(src ? 'loading' : 'error');
    }, [src]);
    if (status === 'error') return null;
    return (
      <img
        ref={ref}
        src={src}
        className={cn('aspect-square h-full w-full object-cover', className)}
        onLoad={(e) => {
          setStatus('loaded');
          onLoadingStatusChange?.('loaded');
          onLoad?.(e);
        }}
        onError={(e) => {
          setStatus('error');
          onLoadingStatusChange?.('error');
          onError?.(e);
        }}
        {...props}
      />
    );
  },
);
AvatarImage.displayName = 'AvatarImage';

export const AvatarFallback = forwardRef<HTMLSpanElement, HTMLAttributes<HTMLSpanElement>>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        'flex h-full w-full items-center justify-center rounded-full bg-white/[0.06] text-small font-medium text-primary',
        className,
      )}
      {...props}
    />
  ),
);
AvatarFallback.displayName = 'AvatarFallback';
